import { diagnosticOutcome, diagnosticTradeProvider, tradeDiagnostic, tradeDiagnosticWorkId, tradeSpan } from "./trade-diagnostics";
import { sameTradeAddress } from "../config/trade";
import type { Eip1193Provider } from "./eip1193";
import { checkTradeContracts, confirmTradeHead, readTradeHead, type TradeObservation } from "./trade-readiness";
import { DEFAULT_RPC_READ_TIMEOUT_MS, type RpcReadOptions } from "./read-rpc";

type Check = {
  head: TradeObservation; abort: AbortController; promise: Promise<void>;
  diagnosticWorkId: string | undefined; users: number; expiresAt: number; verified: boolean;
};
const sameHead = (a: TradeObservation, b: TradeObservation) =>
  a.blockNumber === b.blockNumber && sameTradeAddress(a.blockHash, b.blockHash);

/** One provider/deployment/session owner. Never used by transaction preparation. */
export class QuoteContractChecks {
  private entry: Check | null = null;
  private pendingReads = new Set<Promise<void>>();
  private observations = new WeakMap<TradeObservation, Check>();

  constructor(private readonly providerForSignal: (signal: AbortSignal) => Eip1193Provider,
    readonly multicall = false) {}

  clear(): void {
    const entry = this.entry;
    this.entry = null;
    entry?.abort.abort(new Error("QUOTE_CONTRACT_CONTEXT_CHANGED"));
  }

  async drain(): Promise<void> { await Promise.all(this.pendingReads); }

  /** Optional quote-only subscriber. No head read, no renewal on a cache hit. */
  async prewarm(head: TradeObservation, guard: () => void, options: RpcReadOptions = {}): Promise<void> {
    options.signal?.throwIfAborted(); guard();
    let entry = this.entry;
    // A late prefetch must not evict a newer quote's verification.
    if (entry && head.blockNumber < entry.head.blockNumber) return;
    if (entry && ((head.blockNumber === entry.head.blockNumber && !sameHead(head, entry.head)) ||
      (entry.verified && performance.now() >= entry.expiresAt))) {
      this.clear(); entry = null;
    }
    if (entry?.verified) return;
    const used = entry ?? this.start(head, options.diagnosticOperationId);
    tradeDiagnostic(options.diagnosticOperationId, "quote-contract-subscriber", { workId: used.diagnosticWorkId });
    await this.join(used, options.signal);
    options.signal?.throwIfAborted(); guard();
  }

  /** A final quote reorg invalidates only the verification used by that quote. */
  invalidate(observation: TradeObservation): void {
    const used = this.observations.get(observation);
    if (used && this.entry === used) this.clear();
  }

  private start(head: TradeObservation, operationId?: string): Check {
    const entry: Check = { head, diagnosticWorkId: tradeDiagnosticWorkId(), abort: new AbortController(), promise: Promise.resolve(), users: 0, expiresAt: 0, verified: false };
    this.entry = entry;
    const guard = () => entry.abort.signal.throwIfAborted();
    entry.promise = (async () => {
      try {
        // Cancelled wallet reads cannot be unsent; replacements share this bounded drain.
        const drainEnd = tradeSpan(operationId, "contract-drain", { workId: entry.diagnosticWorkId });
        await this.drain(); drainEnd(); guard();
        const source = this.providerForSignal(entry.abort.signal);
        const tracked: Eip1193Provider = { request: args => {
          guard();
          const request = source.request(args);
          const settled = new Promise<void>(resolve => {
            const timer = setTimeout(resolve, DEFAULT_RPC_READ_TIMEOUT_MS);
            const finish = () => { clearTimeout(timer); resolve(); };
            request.then(finish, finish);
          });
          this.pendingReads.add(settled);
          const remove = () => { this.pendingReads.delete(settled); };
          settled.then(remove, remove);
          return request;
        } };
        const provider = diagnosticTradeProvider(tracked, operationId, source, entry.diagnosticWorkId);
        const options = { signal: entry.abort.signal };
        await checkTradeContracts(provider, head, guard, options, this.multicall);
        await confirmTradeHead(provider, head, guard, options);
        guard();
        entry.verified = true;
        entry.expiresAt = performance.now() + 30_000;
      } catch (error) {
        if (this.entry === entry) this.entry = null;
        entry.abort.abort(error);
        throw error;
      }
    })();
    return entry;
  }

  private async join(entry: Check, signal?: AbortSignal): Promise<void> {
    entry.users++;
    let cancel = () => {};
    try {
      await new Promise<void>((resolve, reject) => {
        cancel = () => reject(signal?.reason);
        signal?.addEventListener("abort", cancel, { once: true });
        entry.promise.then(resolve, reject);
        if (signal?.aborted) cancel();
      });
    } finally {
      signal?.removeEventListener("abort", cancel);
      entry.users--;
      if (!entry.users && !entry.verified && this.entry === entry) this.clear();
    }
  }

  async observe(provider: Eip1193Provider, guard: () => void, options: RpcReadOptions = {},
    freshHead?: TradeObservation): Promise<TradeObservation> {
    const end = tradeSpan(options.diagnosticOperationId, "quote-contract-checks");
    let used = this.entry;
    try {
      options.signal?.throwIfAborted(); guard();
      // The early-preview reader supplies the same freshly validated head to
      // pricing and admission, avoiding a second latest-block observation.
      const head = freshHead ?? await readTradeHead(provider, guard, options);
      options.signal?.throwIfAborted(); guard();
      let entry = this.entry;
      if (entry && (head.blockNumber < entry.head.blockNumber ||
        (head.blockNumber === entry.head.blockNumber && !sameHead(head, entry.head)) ||
        (entry.verified && performance.now() >= entry.expiresAt))) {
        this.clear(); entry = null;
      }
      const hit = Boolean(entry?.verified);
      used = entry ?? this.start(head, options.diagnosticOperationId);
      tradeDiagnostic(options.diagnosticOperationId, "quote-contract-subscriber", { workId: used.diagnosticWorkId });
      if (!hit) await this.join(used, options.signal);
      options.signal?.throwIfAborted(); guard();
      // Cache hits rely on the quote's final block check. Cold/shared checks
      // still confirm their head before the verification can be reused.
      if (!hit && !sameHead(head, used.head)) await confirmTradeHead(provider, head, guard, options);
      const observation = { ...head, observedAt: performance.now() };
      this.observations.set(observation, used);
      end("ok", { workId: used.diagnosticWorkId, blockNumber: `0x${head.blockNumber.toString(16)}`, blockHash: head.blockHash });
      return observation;
    } catch (error) {
      end(diagnosticOutcome(error));
      // A cancelled subscriber must not poison another subscriber's work.
      let current = !options.signal?.aborted;
      try { guard(); } catch { current = false; }
      if (current && used && this.entry === used) this.clear();
      throw error;
    }
  }
}
